// トレーサビリティ情報一覧 (材料単位) — 発注者提出系
// MaterialQr 毎に 1 行、46 列で発行〜出荷〜搬入〜施工〜消費を一覧化。

import ExcelJS from "exceljs";
import type { ExtendedDB } from "../../../../types/extendedDB";
import {
  buildLookups, downloadBlob, todayStr, setHeaderRow,
  formatQrCode, fmtDate, fmtTime, fmtDateTime, round1,
  calcExpiryDate, processAreaIdOf,
} from "./helpers";
import type { ConsumptionEntry, Lookups } from "./helpers";

type Work = NonNullable<ReturnType<Lookups["workById"]["get"]>>;

function lookupById<K, V>(map: Map<K, V>, key: K | undefined | null): V | undefined {
  if (key == null) {
    return undefined;
  }
  return map.get(key);
}

function lookupListById<K, V>(map: Map<K, V[]>, key: K | undefined | null): V[] {
  if (key == null) {
    return [];
  }
  return map.get(key) ?? [];
}

function joinDetailNames(details: ExtendedDB["materialDetails"]): string {
  if (details.length === 1) {
    return details[0]?.detail_name ?? "";
  }
  return details.map((d) => d.detail_name).join("+");
}

function selectTargetQrs(db: ExtendedDB, lk: Lookups, selectedWork: Work | undefined): ExtendedDB["materialQrs"] {
  if (!selectedWork) {
    return db.materialQrs;
  }
  return db.materialQrs.filter((q) => {
    if (q.carry_in_id == null) {
      return false;
    }
    const ci = lk.carryInById.get(q.carry_in_id);
    return ci?.work_id === selectedWork.id;
  });
}

// number(epoch) を ISO 文字列に変換。fmtTime/fmtDateTime は文字列前提のため変換しないと正しく整形できない。
function epochToIso(v: number | null | undefined): string | undefined {
  if (v == null) return undefined;
  return new Date(v).toISOString();
}

export async function exportTraceabilityByMaterial(db: ExtendedDB, workId?: number): Promise<void> {
  const lk = buildLookups(db);
  const selectedWork = lookupById(lk.workById, workId);
  const wb = new ExcelJS.Workbook();
  wb.creator = "奥村組 トレーサビリティシステム";
  const ws = wb.addWorksheet("トレーサビリティ情報一覧");

  const headers: Array<[string, number]> = [
    ["QRコード", 18], ["LotNo", 14], ["管理番号", 18], ["材料名", 26], ["材料種別名", 14], ["工事名", 32],
    ["搬入日", 12], ["搬入時刻", 10], ["搬入場所名", 22],
    ["立会(施主)_搬入", 16], ["立会(受注)_搬入", 16],
    ["製造年月日", 12], ["出荷日", 12], ["使用期限日", 12],
    ["備考(搬入情報)", 22], ["備考(搬入実績)", 22], ["写真(搬入)", 10], ["搬入箇所", 14],
    ["施工日", 12], ["使用量", 10],
    ["区分1", 14], ["区分2", 12], ["区分3", 12], ["区分4", 12], ["区分5", 12],
    ["区分6", 12], ["区分7", 12], ["区分8", 12], ["区分9", 12], ["区分10", 12],
    ["立会(施主)_施工", 16], ["立会(受注)_施工", 16],
    ["温度(℃)", 10], ["湿度(%)", 10],
    ["工種", 14], ["種別", 14], ["工程", 18], ["施工方法", 14], ["撹拌方法", 14],
    ["攪拌開始時刻", 18], ["施工開始時刻", 18], ["施工終了時刻", 18],
    ["備考(施工情報)", 22], ["備考(施工実績)", 22], ["写真(施工)", 10], ["施工画面リンク", 30],
  ];
  headers.forEach((h, i) => {
    const c = ws.getCell(1, i + 1);
    c.value = h[0];
    ws.getColumn(i + 1).width = h[1];
  });
  setHeaderRow(ws, 1);

  let rowIdx = 2;
  const targetQrs = selectTargetQrs(db, lk, selectedWork);
  const qrs = targetQrs.slice().sort((a, b) => a.id - b.id);

  for (const qr of qrs) {
    const lot = lookupById(lk.lotById, qr.lot_id);
    const material = lookupById(lk.materialById, lot?.material_id ?? undefined);
    const materialDetails = lookupListById(lk.materialDetailsByMaterialId, lot?.material_id ?? undefined);
    const materialDetailName = joinDetailNames(materialDetails);
    const carryOut = lookupById(lk.carryOutById, lot?.carry_out_id);
    const carryIn = lookupById(lk.carryInById, qr.carry_in_id);
    const work = lookupById(lk.workById, carryIn?.work_id ?? undefined);
    const carryInLocation = lookupById(lk.carryInLocationById, carryIn?.carry_in_location_id ?? undefined);
    const expiryDate = calcExpiryDate(lot?.manufactured_at != null ? fmtDate(lot.manufactured_at) : undefined, materialDetails[0]?.shelf_life_months);

    // QR×消費施工で複数行に展開する(施工日昇順)。消費が無いQRは施工列が空の1行のみ出力する。
    // 消費は ConstructionMaterialConsumption(真ソース)のみで判定する(旧データは migration で消費行へ変換済み)。
    // 消費行が1件も無い QR は未消費の1行として出力する。
    const consumptionEntries = lookupListById(lk.consumptionsByQrId, qr.id).slice()
      .sort((a, b) => (lk.constructionById.get(a.constructionId)?.executed_at ?? 0) - (lk.constructionById.get(b.constructionId)?.executed_at ?? 0));
    let entryRows: (ConsumptionEntry | undefined)[] = consumptionEntries;
    if (consumptionEntries.length === 0) {
      entryRows = [undefined];
    }
    // QR単位で Σ usedRatio が 1 を超える(毒データ)場合、各行の按分比を保ったまま比例縮小して
    // QR単位の合計が 1 を超えないようにする(行を分けて出す仕様自体は維持する)。
    const consumptionRawTotal = consumptionEntries.reduce((a, e) => a + e.usedRatio, 0);
    const consumptionClampedTotal = Math.min(1, Math.max(0, consumptionRawTotal));
    const consumptionScale = consumptionRawTotal > 0 ? consumptionClampedTotal / consumptionRawTotal : 1;

    for (const entry of entryRows) {
      const constructionId = entry?.constructionId;
      const construction = lookupById(lk.constructionById, constructionId);
      const processes = lookupListById(lk.processesByConstructionId, construction?.id);
      const process = processes[0];
      const stir = lookupById(lk.stirByProcessId, process?.id);
      const wk = lookupById(lk.workExecByProcessId, process?.id);
      const wd = lookupById(lk.workDetailById, process?.work_detail_id ?? undefined);
      const wst = lookupById(lk.workSubtypeById, wd?.work_subtype_id);
      const wt = lookupById(lk.workTypeById, wst?.work_type_id);
      const mixDef = lookupById(lk.mixRatioDefById, stir?.mix_ratio_definition_id);
      const cm = lookupById(lk.constructionMethodById, mixDef?.construction_method_id ?? undefined);
      const sm = lookupById(lk.stirringMethodById, mixDef?.stirring_method_id ?? undefined);
      const area = lookupById(lk.planAreaById, processAreaIdOf(process));
      // area_info_1〜5 を区分1〜5 にマップ。区分6〜10 は API スキーマに直接フィールドが無いため "-"。
      const slot1 = area?.area_info_1 ?? "";
      const slot2 = area?.area_info_2 ?? "";
      const slot3 = area?.area_info_3 ?? "";
      const slot4 = area?.area_info_4 ?? "";
      const slot5 = area?.area_info_5 ?? "";
      // 使用量(袋数)。entry が無い(未消費QR)行は空欄。0kg(紐付けのみ)は 0 を出力する。
      const usageBags = entry != null ? Math.round(entry.usedRatio * consumptionScale * 100) / 100 : "";

      const values: Array<string | number | undefined> = [
        formatQrCode(qr.id),
        lot?.lot_no ?? "",
        qr.id.toString().padStart(8, "0"),
        material?.name ?? "",
        materialDetailName,
        work?.official_name ?? "",
        fmtDate(carryIn?.received_at),
        fmtTime(epochToIso(carryIn?.received_at)),
        carryInLocation?.name ?? "",
        "-",
        "-",
        fmtDate(lot?.manufactured_at),
        carryOut ? fmtDate(lot?.shipped_at) : "",
        expiryDate,
        lot?.remarks ?? "",
        carryIn?.remarks ?? "",
        "-",
        "-",
        fmtDate(construction?.executed_at),
        usageBags,
        slot1, slot2, slot3, slot4, slot5,
        "-", "-", "-", "-", "-",
        "-", "-",
        round1(construction?.ambient_temperature),
        round1(construction?.ambient_humidity),
        wt?.name ?? "",
        wst?.name ?? "",
        wd?.name ?? "",
        cm?.name ?? "",
        sm?.name ?? "",
        fmtDateTime(stir?.stir_started_at),
        fmtDateTime(wk?.construction_started_at ?? stir?.stir_finished_at),
        fmtDateTime(wk?.construction_finished_at ?? (construction?.finished_at != null ? epochToIso(construction.finished_at) : undefined)),
        area?.remarks ?? "",
        process?.remarks ?? construction?.remarks ?? "",
        "-",
        construction ? `/console/construction-execute/${construction.id}/` : "",
      ];

      values.forEach((v, i) => {
        ws.getCell(rowIdx, i + 1).value = v ?? "";
      });
      rowIdx++;
    }
  }

  if (qrs.length === 0) {
    ws.getCell(2, 1).value = "※ 材料QR データが存在しません";
    ws.getCell(2, 1).font = { italic: true };
  }

  const buffer = await wb.xlsx.writeBuffer();
  downloadBlob(buffer, `トレーサビリティ情報一覧_${todayStr()}.xlsx`);
}
